import React from 'react';
import { useParams } from 'react-router-dom';
import { Contract } from '../types';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  ArrowsUpDownIcon,
  ArrowTopRightOnSquareIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import {
  contractColumns,
  formatDate,
  formatDueTime,
  formatDueSummary,
} from '../utils/contractFormatting';
import { getProcuringTypeMeta } from '../utils/procuringTypes';
import { DEFAULT_REGION } from '../regions';
import TruncatedTitleWithTooltip from './TruncatedTitleWithTooltip';
import OpportunityTypeBadge from './OpportunityTypeBadge';
import SaveOpportunityButton from './SaveOpportunityButton';
import { trackEvent } from '../utils/analytics';
import MatchReasons from './MatchReasons';
import { getOpportunitySourceAriaLabel, getOpportunitySourceName } from '../utils/opportunitySources';

interface ContractTableProps {
  contracts: Contract[];
  onSort: (field: string) => void;
  sortField: string;
  sortDirection: 'asc' | 'desc';
  browseSearch?: string;
  saveSurface?: 'table' | 'saved_page';
  showMatchReasons?: boolean;
}

const ContractTable: React.FC<ContractTableProps> = ({
  contracts,
  onSort,
  sortField,
  sortDirection,
  browseSearch = '',
  saveSurface = 'table',
  showMatchReasons = false,
}) => {
  const { region } = useParams();
  const targetRegion = region ?? DEFAULT_REGION;
  const columnIcons: Record<string, React.ReactNode | null> = {
    title: null,
    industry: <BuildingOfficeIcon className="h-4 w-4 text-slate-400" />, 
    city: <MapPinIcon className="h-4 w-4 text-slate-400" />, 
    due_date: <CalendarIcon className="h-4 w-4 text-slate-400" />, 
  };

  const SortableHeader: React.FC<{ field: string; label: string }> = ({ field, label }) => {
    const isSorted = sortField === field;

    return (
      <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">
        <button
          type="button"
          onClick={() => onSort(field)}
          className="group flex items-center gap-2 whitespace-nowrap text-left focus:outline-none"
        >
          {columnIcons[field] && <span className="text-slate-400">{columnIcons[field]}</span>}
          <span className="text-slate-600 group-hover:text-slate-900">{label}</span>
          {isSorted ? (
            sortDirection === 'asc' ? (
              <ChevronUpIcon className="h-4 w-4 text-blue-500 transition" />
            ) : (
              <ChevronDownIcon className="h-4 w-4 text-blue-500 transition" />
            )
          ) : (
            <ArrowsUpDownIcon className="h-4 w-4 text-slate-300 group-hover:text-slate-400 transition" />
          )}
        </button>
      </th>
    );
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-slate-50">
            <tr>
              <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">
                Save
              </th>
              {contractColumns.map((column) => (
                <SortableHeader key={column.key} field={column.key} label={column.label} />
              ))}
              <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">
                View Source
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {contracts.map((contract, index) => {
              const procuringOrg = contract.procuring_organization || contract.city;
              const procuringMeta = getProcuringTypeMeta(contract.procuring_type);
              const dueTime = formatDueTime(contract.due_at);
              const dueSummary = formatDueSummary(contract);

              return (
              <tr
                key={`${contract.title}-${index}`}
                className="transition hover:bg-slate-50"
              >
                <td className="px-4 py-4 align-top">
                  <SaveOpportunityButton opportunity={contract} surface={saveSurface} compact />
                </td>
                <td className="max-w-xs px-6 py-4 align-top">
                  {contract.id ? (
                    <TruncatedTitleWithTooltip
                      title={contract.title}
                      to={`/${targetRegion}/opportunities/${contract.id}${browseSearch}`}
                      className="font-semibold text-slate-900 line-clamp-2 hover:text-blue-600"
                    />
                  ) : (
                    <TruncatedTitleWithTooltip
                      title={contract.title}
                      className="font-semibold text-slate-900 line-clamp-2"
                    />
                  )}
                  {contract.opportunity_type && <div className="mt-2"><OpportunityTypeBadge value={contract.opportunity_type} /></div>}
                  {showMatchReasons && <MatchReasons reasons={contract.match_reasons} className="mt-2" />}
                </td>
                <td className="px-6 py-4 text-sm text-slate-600 align-top">{contract.industry}</td>
                <td className="px-6 py-4 align-top">
                  <div className="flex flex-col gap-1">
                    <span className="text-sm font-medium text-slate-700">{procuringOrg}</span>
                    {procuringMeta && (
                      <div
                        className="inline-flex items-center gap-2 text-xs font-medium text-slate-500"
                        title={procuringMeta.tooltip || procuringMeta.label}
                      >
                        <img
                          src={procuringMeta.icon}
                          alt={procuringMeta.label}
                          className="h-4 w-4 shrink-0 object-contain"
                        />
                        <span className="leading-tight">{procuringMeta.label}</span>
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-slate-600 align-top">
                  <div>{formatDate(contract.due_date)}</div>
                  {dueTime && <div className="text-xs font-medium text-slate-500">{dueTime}</div>}
                  {dueSummary && (
                    <div className="text-xs text-slate-500">{dueSummary}</div>
                  )}
                </td>
                <td className="px-6 py-4 text-right align-top">
                  <div className="flex items-center justify-end">
                    <a
                      href={contract.source_url}
                      onClick={() => trackEvent('saved_opportunity_source_clicked', { contract_id: contract.id, surface: saveSurface })}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      aria-label={getOpportunitySourceAriaLabel(contract.source_type)}
                      className="inline-flex min-h-10 min-w-[108px] items-center justify-center gap-1 whitespace-nowrap rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-900"
                    >
                      {getOpportunitySourceName(contract.source_type)}
                      <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                    </a>
                  </div>
                </td>
              </tr>
            )})}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ContractTable;
